/**
 * Narrows an untrusted `redirectUrl` to a same-origin relative path.
 *
 * `redirectUrl` reaches the sign-in page from the query string, so it is
 * attacker-controllable: a driver can be handed a link to our own sign-in page
 * that carries an off-site destination. Both consumers navigate to that value —
 * one through a <Link>, one through a server-side redirect via
 * /api/v1/check-user-session — so neither can trust it unchecked.
 *
 * Returns the path when it is safe, or `null` when it is not, so a rejected
 * value degrades to exactly the same behavior as no `redirectUrl` at all.
 *
 * Rejected:
 *   - absolute URLs        https://evil.example
 *   - protocol-relative    //evil.example
 *   - backslash variants   /\evil.example  (some browsers read \ as /)
 *   - anything not beginning with a single "/"
 */
function safeRedirectPath(value: string | null | undefined): string | null {
  if (!value) return null;
  if (!value.startsWith('/')) return null;
  // Reject "//host" and "/\host", both of which resolve off-origin.
  if (/^\/[/\\]/.test(value)) return null;

  return value;
}

export default safeRedirectPath;
